import type { ComponentType } from 'react'
import type { FilterOp } from '../api/schemas'
import type { PlaceDetails } from '../api/client'
import type { PlaceRole } from '../places/places'

export interface CellContentProps {
  value: unknown
  /** The attribute's `config` (e.g. select `options`) — read widgets that need
   *  option metadata (colours, labels) read it here. Optional: widgets that
   *  ignore config are unaffected. Mirrors CellEditorProps.config. */
  config?: Record<string, unknown>
  /** Navigation callback — entity-reference widgets use this to make chips
   *  navigable (click opens the target record). When present, chips render as
   *  focusable buttons with cursor-pointer + hover:underline affordance.
   *  Most widgets ignore this field entirely. */
  onNavigate?: (typeId: string, recordId: string) => void
  /** SEN-1766: the column entry's declared presentation variant, or null/absent
   *  for the widget's default. A widget that declares no `formats` ignores it. */
  format?: string | null
}

/** SEN-1766: one DECLARED presentation variant of a kind's cell. `value` is the
 *  wire token stored on a column entry's leaf hop and hashed into its
 *  column_id; `label` is what a person reads. Mirrors the backend's
 *  AttributeTypeSpec.formats — the widget declares the variants it can DRAW. */
export interface FormatVariant {
  value: string
  label: string
}

/**
 * A kind-specific editor. Mounted in place of the read content while editing;
 * commits the new value (Enter / blur) or cancels (Esc). The mutation itself
 * (persist + cache reconcile) lives at the table level — the editor only
 * produces the next value.
 */
export interface CellEditorProps {
  value: unknown
  onCommit: (next: unknown) => void
  onCancel: () => void
  /** The attribute's `config` (e.g. select `options`) — editors that need
   *  metadata to render their control (the select dropdown) read it here. */
  config?: Record<string, unknown>
  /** Rendering context:
   *  - 'cell'   (default): mounted = editing; popover may defaultOpen; commit unmounts.
   *  - 'filter': no "+ Create" row; live-commit on change (SEN-704).
   *  - 'form':   closed trigger on mount; opens on click; commit closes the popover
   *              but does NOT unmount the editor; native DOM-order tabbable control. */
  context?: 'cell' | 'filter' | 'form'
  /** Tenant slug — editors that fetch (entity-reference record search) need it.
   *  Optional: scalar editors ignore it. */
  slug?: string
  /** Type id of the attribute's owning type — select editors that have no
   *  config.options fetch real options from the API and need this. Optional:
   *  editors that don't fetch (text, number…) ignore it. */
  typeId?: string
  /** Attribute id — select editors use it together with typeId to fetch
   *  /select-options when config.options is absent. Optional. */
  attributeId?: string
}

/** A filter operator a type supports, with its display label. `needsValue` is
 *  false for empty / non-empty (no value editor shown). */
export interface FilterOperator {
  op: FilterOp
  label: string
  needsValue: boolean
}

/**
 * A widget = how one attribute KIND renders its content, and (optionally) how
 * it is edited. The generic Cell container owns interaction (click highlight,
 * expand, double-click-to-edit); a widget only renders content / its editor.
 * Adding a kind = one registration. A kind with no `write` is read-only — which
 * is also how derived (compute-on-read) attributes stay non-editable.
 */
export interface WidgetSpec {
  read: ComponentType<CellContentProps>
  /** The read view for a ROOMY surface (a record page's property row), where a
   *  table cell's one line would waste the space. Absent → `read`. */
  detail?: ComponentType<CellContentProps>
  /** Overlay editor (text, number, select, date…): double-click → edit → commit. */
  write?: ComponentType<CellEditorProps>
  /** Direct flip (boolean): double-click commits `toggle(value)` with no editor —
   *  a checkbox has no intermediate "edit" state. Takes precedence over `write`. */
  toggle?: (value: unknown) => unknown
  /** Filter-OPERAND editor for a kind whose CELLS are read-only but whose
   *  declared filter_operators need a comparison value (calculated: numeric
   *  gt/lt/…). FilterValue prefers this over `write`; cell editing is
   *  untouched — a read-only kind stays read-only (SEN-1784 known-gap:
   *  an advertised operator must be completable, never a dead condition). */
  filterOperand?: ComponentType<CellEditorProps>
  align?: 'right' | 'center'
  /** Filter operators this kind supports. Present ⇒ the attribute is filterable.
   *  Absent (reference, json) ⇒ auto-excluded from the filter builder. */
  operators?: FilterOperator[]
  /** Default column width (px) for table-layout: fixed when the view has no
   *  saved width for the column. Per-kind so number/date are narrower than text. */
  defaultWidth?: number
  /** Presentation variants this widget can draw. Offered verbatim by the column
   *  header menu — a variant the widget cannot render is never on screen. */
  formats?: readonly FormatVariant[]
  /** The read widget draws a SET of this kind's values as one summary — a
   *  `{ set: true, values, more }` value (a path column reaching many records,
   *  records/values' `{values, more}` answer). Kinds without it render a set
   *  value by value. Declared, so a caller never branches on the kind. */
  readsSets?: boolean
  /** The read widget captions its number with the attribute's DECLARED unit
   *  (declaredUnit: kWh, m², kWh/m²). Declared so the column header can drop
   *  a unit its cells already show (SEN-226) — never a branch on the kind. */
  showsUnit?: boolean
  /** The value of this kind a picked Google place carries for a field, or
   *  undefined when it carries none (2026-10-23). The create form fills each
   *  EMPTY field through this, told the field's declared role and whether it is
   *  the primary attribute — so what a place fills is declared per kind, never
   *  a branch on a slug or a kind name in the form. */
  fromPlace?: (place: PlaceDetails, role: PlaceRole) => unknown
}

const registry = new Map<string, WidgetSpec>()

export function registerWidget(kind: string, spec: WidgetSpec): void {
  registry.set(kind, spec)
}

const hintRegistry = new Map<string, WidgetSpec>()

/** Register a generic read widget under a render_hint value (link / mono /
 *  plain / unit / percentage). Consumed only as a FALLBACK when a kind has no
 *  own registration — i.e. a tenant subtype minted via SEN-735. The 19 globals
 *  keep their per-kind registrations and never reach this path. */
export function registerHintWidget(hint: string, spec: WidgetSpec): void {
  hintRegistry.set(hint, spec)
}

export function resolveWidget(kind: string, renderHint?: string | null): WidgetSpec | undefined {
  return registry.get(kind) ?? (renderHint ? hintRegistry.get(renderHint) : undefined)
}

/** Returns the list of all registered kind strings. Used by the form-context
 *  contract test to iterate every registered write editor without hardcoding
 *  a list — new registrations automatically join the contract. */
export function registeredKinds(): string[] {
  return Array.from(registry.keys())
}
